import { IImportable } from 'library/ts/interfaces/importable/IImportable';
import { IImportVO } from 'library/ts/interfaces/importer/IImportVO';
import { IProjectInfo } from 'library/ts/IProjectInfo';
import { ImportType } from 'library/ts/model/enums/config/ImportType';
import { AssetEmbedType } from 'library/ts/model/enums/content/AssetEmbedType';
import { Country } from 'library/ts/model/enums/geo/Country';
import { Partner } from 'library/ts/model/enums/partner/Partner';
import { SubscriberPackageDelimiter } from 'library/ts/model/user/SubscriberPackageDelimiter';
import { SubscriberPackageKey } from 'library/ts/model/user/SubscriberPackageKey';
import { SubscriberPackageValue } from 'library/ts/model/user/SubscriberPackageValue';
import { UserType } from 'library/ts/model/user/UserType';
import { UserTypeLibrary } from 'library/ts/model/user/UserTypeLIbrary';
import { ImportVO } from 'library/ts/model/vo/ImportVO';
import { PlayerAdapterNetworkErrorCodes } from 'library/ts/player/adapter/enums/errors/PlayerAdapterNetworkErrorCodes';
import { IPlayerAdapterErrorInfo } from 'library/ts/player/adapter/interfaces/errors/IPlayerAdapterErrorInfo';
import { TSubscriber } from 'library/ts/types/TSubscriber';
import { ISmartTagRuntimeVO } from 'tag/ts/smart_tag/interfaces/ISmartTagRuntimeVO';
import { AbstractTagClass } from 'tag/ts/tag/abstract/AbstractTagClass';
import { ITagAPIOut } from 'tag/ts/tag/interfaces/api/ITagAPIOut';
import { ITagAbstractClassArgument } from 'tag/ts/tag/interfaces/arguments/ITagAbstractClassArgument';
import { ICallbacksVO } from 'tag/ts/tag/interfaces/callbacks/ICallbacksVO';
import { IImports } from 'tag/ts/tag/interfaces/config/imports/IImports';
import { ITag } from 'tag/ts/tag/interfaces/ITag';
import { ICP } from 'tag/ts/tag/interfaces/metadata/cp/ICP';
import { IParamsVO } from 'tag/ts/tag/interfaces/params/IParamsVO';
import { ITagUtilities } from 'tag/ts/tag/interfaces/utilities/ITagUtilities';
import { AdSDK } from 'tag/ts/tag/model/type/ads/AdSDK';
import { EmbedDeviceVersion } from 'tag/ts/tag/model/type/embed/EmbedDeviceVersion';
import { EmbedType } from 'tag/ts/tag/model/type/embed/EmbedType';
import { SkinMode } from 'tag/ts/tag/model/type/SkinMode';
import { TTagBrand } from 'tag/ts/tag/types/TTagBrand';
import { getBrand as resolveBrand } from 'tag/ts/tag/utilities/BrandResolver';

/**
 * @copyright © Paramount Streaming
 * @author  Stan Gletski
 * @description A set of Tag utilities to handle various useful tasks
 */
export class TagUtilities extends AbstractTagClass implements ITagUtilities {

	constructor( argument: ITagAbstractClassArgument ) {
		super(
			{
				...argument,
				name: 'TagUtilities'
			}
		);
	}

	/**
	 * @description Returns calculated UserType value derived from paramsVO.USER.userStatus
	 * @param {string} userStatus
	 * @returns {UserType}
	 */
	getUserType = ( userStatus: string = this?.params?.USER?.userStatus ): UserType => {
		// undefined + false userStatus use case
		if ( this.utils.Type.isEmpty( userStatus ) ) return UserType.ANON;
		// set default output user type
		let outputUserType: UserType = UserType.ANON;
		/**
		 * @description Checks if user type (status) passed in via params is present in array of variants
		 * @param {string} paramsVOUserStatus
		 * @param {TSubscriber[]} oneUserTypeVariants
		 * @returns {boolean}
		 */
		const isUserTypePresent = (
			paramsVOUserStatus: string = `${SubscriberPackageKey.ANON}${SubscriberPackageDelimiter.EMPTY}${SubscriberPackageValue.EMPTY}`,
			oneUserTypeVariants: TSubscriber[] = []
		): boolean => {
			for ( let i = 0; i < oneUserTypeVariants.length; i++ ) {
				if ( paramsVOUserStatus.indexOf( oneUserTypeVariants[ i ] ) > -1 ) return true;
			}
			return false;
		};

		for ( const oneUserType of Object.keys( UserTypeLibrary ) ) {
			// if user status value was found in our user type library for the currently checked user type
			if ( isUserTypePresent( userStatus, UserTypeLibrary[ oneUserType as UserType ] ) === true ) {
				// set our output value
				outputUserType = oneUserType as UserType;
				// break loop
				break;
			}
		}

		this?.logger?.log( 'getUserType: return value:', outputUserType );

		return outputUserType;
	};

	/**
	 * @description International context check. Is Smart Tag running on international web pages?
	 * @returns {boolean}
	 */
	isInternationalContext = ( tag: ITag = this.tag ): boolean => {
		let international = false;
		const TAG_API: ITagAPIOut = tag?.controller?.tagAPI?.OUT;

		/**
		 * @description Check user country
		 * @see {@link https://jira.cbsi.com/browse/CBSVIDEO-4140}
		 * @see {@link https://jira.cbsi.com/browse/PPLUSINTL-3967}
		 * @type {boolean}
		 */
		let checkIntlContext = true;
		if ( !this.utils.Type.isEmpty( this.params.CONTENT?.country ) ) {
			switch ( this.params.CONTENT.country ) {
				case Country.LOCALHOST:
				case Country.US:
					checkIntlContext = false;
					break;
			}
		}
		// if we have to check int context
		if ( checkIntlContext === true ) {
			international = TAG_API?.checkContext( { country: this.params.CONTENT.country } );
			if ( this.utils.Type.isUndefined( international ) === true ) international = false;
		}
		return international;
	};

	/**
	 * @description Processes Tag imports converting import URLs to SSL and relative URL if needed
	 * @param {IImportVO[]} imports
	 * @returns {IImportVO[]}
	 */
	processImports = ( imports: IImportVO[] = [] ): IImportVO[] => {
		const runtimeVO = window.SmartTag.runtimeVO;
		imports.forEach(
			( importIO ) => {
				// ssl conversion
				if ( runtimeVO.top.ssl ) importIO.url = this.utils.URL.toHTTPS( importIO.url );
				// if relative URL is requested => convert dependency URL to relative URL
				if ( runtimeVO?.readyCallback?.payload?.relative === true ) importIO.url = this.utils.URL.convertToRelative( importIO?.url );
			}
		);
		return imports;
	};

	/**
	 * @description Returns a list of dependencies for a given Tag instance
	 * @param {ITag} tag
	 * @return {IImportVO[]}
	 */
	getImports = ( tag: ITag = this.tag ): IImportVO[] => {
		const runtimeVO = this.runtimeVO;
		const callbackImports = runtimeVO?.readyCallback?.payload?.imports;
		const paramsVO = tag?.params;
		const importsModule = tag?.model?.config?.tag?.Imports?.params; // Imports specified in Tag config Imports module
		let dependencies: IImportVO[] = []; // create placeholder array
		const adSetupInfo = tag?.model?.adUtilities?.adSetupInfo();
		const adImportable: IImportable = adSetupInfo?.sdk?.module; // pull Ad SDK module

		// IMA / DAI SDK

		// IMPORTANT: if we have IMA setup + no FMS data passed from page => re-enable ad sdk preload because ad adapter plugin is turned off
		if ( adSetupInfo?.sdk?.type === AdSDK.IMA && this.utils.Type.isEmpty( paramsVO?.AD?.FW?.FMS ) ) {
			paramsVO.AD.SDK.preload = true; // force enable ad sdk preload by Smart Tag
		}

		// If ad SDK to be loaded by Smart Tag + ad import module is defined + is not present in DOM
		if (
			paramsVO?.AD?.SDK?.preload === true
			&&
			this.utils.Type.isDefined( adImportable )
			&&
			!this.utils.Import.alreadyPresent( adImportable.url.regular, adImportable.namespace )
		) {
			// add it to dependencies array
			dependencies.push(
				new ImportVO( ImportType.SCRIPT, adImportable.url.regular, adImportable.namespace, window )
			);
		}

		// Desktop Skin and the custom Mobile Skin both load only when SkinModeResolver selected
		// them — see that class for the single place this decision (vs avia-ui, the Mobile Web
		// UI plugin, or no skin at all) is made.
		if ( tag?.model?.skinMode === SkinMode.DESKTOP ) {
			this.#addSkinCSS( paramsVO, importsModule, callbackImports, runtimeVO, dependencies, 'skin_css' );
			this.#addSkinScript( importsModule?.smart_tag?.skin, callbackImports?.smart_tag?.skin, runtimeVO, dependencies );
		} else if ( tag?.model?.skinMode === SkinMode.CUSTOM_MOBILE ) {
			this.#addSkinCSS( paramsVO, importsModule, callbackImports, runtimeVO, dependencies, 'skin_mobile_css' );
			this.#addSkinScript( importsModule?.smart_tag?.skin_mobile, callbackImports?.smart_tag?.skin_mobile, runtimeVO, dependencies );
		} else {
			// AVIA_UI, NONE, or unset — no skin app to load here; avia-js-ui and the Mobile
			// Web UI plugin bring in their own dependencies via their own plugin config.
		}
		dependencies = this.processImports( dependencies ); // process dependencies
		return dependencies;
	};

	/**
	 * @description Adds a skin application's own script import (desktop Skin or custom Mobile
	 * Skin) to dependencies, guarding against a duplicate already present in the DOM.
	 * @param {IImportable | undefined} skinImportable - The resolved Imports config entry (`smart_tag.skin` or `smart_tag.skin_mobile`)
	 * @param {IImportable | undefined} callbackSkinImportable - The matching callback-imports entry, if the host provided one
	 * @param {ISmartTagRuntimeVO} runtimeVO - Runtime configuration
	 * @param {IImportVO[]} dependencies - Dependencies array to populate
	 */
	#addSkinScript = (
		skinImportable: IImportable | undefined,
		callbackSkinImportable: IImportable | undefined,
		runtimeVO: ISmartTagRuntimeVO,
		dependencies: IImportVO[]
	): void => {
		const skinAppURL: string = skinImportable?.url?.regular;
		const skinAppNamespace: string = skinImportable?.namespace;
		if (
			!this.utils.Import.alreadyPresent(
				skinAppURL,
				skinAppNamespace,
				!this.utils.URL.valid( callbackSkinImportable?.url?.regular )
			)
		) {
			dependencies.push(
				new ImportVO(
					ImportType.SCRIPT,
					this.utils.URL.addCacheToken(
						skinAppURL, runtimeVO.debug.cacheBust
					),
					skinAppNamespace,
					window
				)
			);
		}
	};

	/**
	 * @description Adds Skin CSS imports (default + brand CSS) to dependencies
	 * @param {IParamsVO} paramsVO - Tag parameters
	 * @param {IImports | undefined} importsModule - Imports module configuration
	 * @param {IImports | undefined} callbackImports - Callback imports
	 * @param {ISmartTagRuntimeVO} runtimeVO - Runtime configuration
	 * @param {IImportVO[]} dependencies - Dependencies array to populate
	 * @param {'skin_css' | 'skin_mobile_css'} cssImportKey - Which Imports config entry to read (desktop Skin vs custom Mobile Skin)
	 */
	#addSkinCSS = (
		paramsVO: IParamsVO,
		importsModule: IImports | undefined,
		callbackImports: IImports | undefined,
		runtimeVO: ISmartTagRuntimeVO,
		dependencies: IImportVO[],
		cssImportKey: 'skin_css' | 'skin_mobile_css'
	): void => {
		// STEP 1: Always load default CSS first (order: 1)
		const defaultCSSURL: string = importsModule?.smart_tag?.[ cssImportKey ]?.url?.regular;

		let defaultCSSImportVO: IImportVO | undefined;
		if (
			!this.utils.Import.alreadyPresent(
				defaultCSSURL,
				undefined,
				!this.utils.URL.valid( callbackImports?.smart_tag?.[ cssImportKey ]?.url?.regular )
			)
		) {
			defaultCSSImportVO = new ImportVO(
				ImportType.STYLE,
				this.utils.URL.addCacheToken( defaultCSSURL, runtimeVO.debug.cacheBust )
			);
			dependencies.push( defaultCSSImportVO );
			this?.logger?.log( 'Loading default skin CSS:', defaultCSSURL );
		}

		// STEP 2: Load brand CSS (runtime CSS custom property overrides)
		// Brand CSS loads after default CSS to override CSS custom properties.
		// Brand CSS URL is derived independently (origin + runtime path, or a DOM stylesheet
		// fallback) — not from defaultCSSImportVO, which #addBrandCSS doesn't reference.
		this.#addBrandCSS( paramsVO, importsModule, runtimeVO, dependencies, defaultCSSImportVO, cssImportKey );
	};

	/**
	 * @description Returns brand identifier based on partner or DOM classes
	 * <p>This function extracts the brand/partner information that is passed into SmartTag from the parent host.</p>
	 * <p>The parent host sets the brand via params.CONTENT.partner when calling SmartTag.create(paramsVO).</p>
	 * <p>If params.CONTENT.partner is not available, it falls back to checking DOM classes on the body element.</p>
	 * <p>This method mirrors SkinUtilities.getBrand() for code clarity and consistency across Tag and Skin modules.</p>
	 *
	 * <p><strong>How brand/partner is passed in:</strong></p>
	 * <ul>
	 *   <li>Parent host calls: <code>SmartTag.create({ CONTENT: { partner: 'pplus' } })</code></li>
	 *   <li>The partner value is stored in <code>window.SmartTag.registry[uuid].params.CONTENT.partner</code></li>
	 *   <li>Default partner value is 'pplus' (defined in ParamsVO.ts)</li>
	 *   <li>Supported values: 'pplus', 'plutotv' (or 'pluto'), 'cbs'</li>
	 * </ul>
	 *
	 * @returns {TTagBrand} Brand identifier ('pplus', 'plutotv', or 'cbs')
	 *
	 * @example
	 * // Usage:
	 * const brand = TagUtilities.getBrand();
	 * // Returns: 'pplus', 'plutotv', or 'cbs'
	 *
	 * @see {@link SkinUtilities.getBrand} For the Skin module's equivalent method
	 */
	public static getBrand = (): TTagBrand => resolveBrand();

	/**
	 * @description Adds brand-specific CSS (runtime CSS custom property overrides) to dependencies if applicable
	 * <p>This method detects the brand from params.CONTENT.partner and loads the appropriate brand CSS file.</p>
	 * <p>Brand CSS files contain CSS custom properties that override default values for brand-specific theming.</p>
	 * <p>Uses TagUtilities.getBrand() for brand detection.</p>
	 *
	 * @param {IParamsVO} paramsVO - Tag parameters
	 * @param {IImports | undefined} importsModule - Imports module configuration
	 * @param {ISmartTagRuntimeVO} runtimeVO - Runtime configuration
	 * @param {IImportVO[]} dependencies - Dependencies array to populate
	 * @param {'skin_css' | 'skin_mobile_css'} cssImportKey - Which Imports config entry to read (desktop Skin vs custom Mobile Skin)
	 *
	 * @see {@link TagUtilities.getBrand} For brand detection logic
	 */
	#addBrandCSS = (
		paramsVO: IParamsVO,
		importsModule: IImports | undefined,
		runtimeVO: ISmartTagRuntimeVO,
		dependencies: IImportVO[],
		defaultCSSImportVO: IImportVO | undefined,
		cssImportKey: 'skin_css' | 'skin_mobile_css'
	): void => {
		// Detect brand using TagUtilities.getBrand() for consistency
		const brand = TagUtilities.getBrand();
		// The app_output_dir/app_output_file entry this CSS's own build output lives under —
		// 'skin' for desktop Skin, 'skin_mobile' for the custom Mobile Skin
		const appOutputDirKey: 'skin' | 'skin_mobile' = cssImportKey === 'skin_mobile_css' ? 'skin_mobile' : 'skin';

		// Load brand CSS if a mapping exists for the resolved brand; otherwise no brand CSS is
		// loaded and default styles apply
		if ( brand ) {
			// Get the brand-specific CSS filename from config
			const brandCSSFilename: string | undefined = importsModule?.smart_tag?.[ cssImportKey ]?.url?.[ brand ];

			// Derive brand CSS URL using the current origin and runtime path
			// This ensures we use the correct domain and path (e.g., localhost, branch domain, /latest/, etc.)
			let brandCSSURL: string | undefined;

			// Primary approach: Construct URL dynamically using origin and runtime path
			// This is the most reliable since it doesn't depend on config URLs or DOM state
			const projectInfo: IProjectInfo = window.PlayerServicesProjectInfo;
			const originURL = this.utils.URL.getOrigin( this.runtimeVO.url.root );

			if ( originURL && brandCSSFilename ) {
				// Extract just the filename from the brand CSS config
				const brandFilename = brandCSSFilename.substring( brandCSSFilename.lastIndexOf( '/' ) + 1 );
				// Extract path from runtime URL root to get envelope path (e.g., "/latest/")
				// runtimeVO.url.root is like "http://localhost/latest/..." or "https://player-services.cbs.com/..."
				const runtimeRootURL = this.runtimeVO.url.root;
				const runtimeRootParsed = this.utils.URL.parser( runtimeRootURL );
				// Get the path from root URL (e.g., "/latest/" or "/")
				const runtimePath = runtimeRootParsed.pathname || '/';
				// Extract envelope path (first segment after root, e.g., "latest")
				const pathSegments = runtimePath.split( '/' ).filter( ( segment ) => segment.length > 0 );
				const envelopePath = pathSegments.length > 0 ? pathSegments[ 0 ] : '';

				// Construct brand CSS URL dynamically: origin + envelope_path + app_output_dir + css/ + filename
				// This matches the structure: http://localhost/latest/smart-tag-skin/css/smart.tag.skin.pplus.css
				const appOutputDir = projectInfo.app_output_dir[ appOutputDirKey ].value;

				// Build the path: envelope_path (e.g., "latest") + app_output_dir (e.g., "smart-tag-skin") + "css/"
				let brandCSSPath = '';
				if ( envelopePath ) {
					brandCSSPath = `/${envelopePath}/${appOutputDir}/css/`;
				} else {
					brandCSSPath = `/${appOutputDir}/css/`;
				}

				// Construct full URL: origin (ends with /) + path (starts with /) = need to remove one slash
				const originWithoutTrailingSlash = originURL.endsWith( '/' ) ? originURL.slice( 0, -1 ) : originURL;
				brandCSSURL = originWithoutTrailingSlash + brandCSSPath + brandFilename;
			} else if ( brandCSSFilename ) {
				// Fallback: Try to find the actual loaded default CSS stylesheet in the DOM
				// (in case it was already loaded from a previous page load)
				const defaultCSSDirName = projectInfo.app_output_dir[ appOutputDirKey ].value;
				const defaultCSSFileName = projectInfo.app_output_file[ appOutputDirKey ].css.min.value;
				let defaultCSSFileURL = this.utils.Import.getFileURL( 'link', [ `/${defaultCSSDirName}/css/`, defaultCSSFileName ] );
				if ( !defaultCSSFileURL ) {
					// Try alternative pattern (without leading slash)
					defaultCSSFileURL = this.utils.Import.getFileURL( 'link', [ `${defaultCSSDirName}/css/`, defaultCSSFileName ] );
				}
				if ( !defaultCSSFileURL ) {
					// Try just the filename
					defaultCSSFileURL = this.utils.Import.getFileURL( 'link', [ defaultCSSFileName ] );
				}

				if ( defaultCSSFileURL ) {
					// Extract the base path from the default CSS URL (everything up to and including the last '/')
					const defaultCSSPath = defaultCSSFileURL.substring( 0, defaultCSSFileURL.lastIndexOf( '/' ) + 1 );
					// Extract just the filename from the brand CSS config (in case it includes a path)
					const brandFilename = brandCSSFilename.substring( brandCSSFilename.lastIndexOf( '/' ) + 1 );
					// Construct the brand CSS URL with the correct environment path and domain
					brandCSSURL = defaultCSSPath + brandFilename;
				} else {
					// Last resort: use brand CSS URL as-is (will likely fail, but preserves original behavior)
					brandCSSURL = brandCSSFilename;
					this?.logger?.warn( 'could not construct brand CSS URL, using config URL as-is:', brandCSSURL );
				}
			}

			// Only load brand CSS if it exists and hasn't been loaded
			if ( brandCSSURL && !this.utils.Import.alreadyPresent( brandCSSURL, undefined, false ) ) {
				dependencies.push(
					new ImportVO(
						ImportType.STYLE,
						this.utils.URL.addCacheToken( brandCSSURL, runtimeVO.debug.cacheBust )
					)
				);
				this?.logger?.log( `Loading ${brand} brand CSS:`, brandCSSURL );
			} else if ( !brandCSSURL ) {
				this?.logger?.log( `Brand ${brand} has no CSS file, using default styles only` );
			} else {
				// Brand CSS already loaded
				this?.logger?.log( `Brand ${brand} CSS already loaded` );
			}
		}
	};

	/**
	 * @description When the host omits {@link IParamsVO.SKIN.cp} or `cp.enabled`, default Continuous Play to on.
	 * Explicit `cp.enabled === false` is unchanged. `endCreditsChapterTime` may stay unset; skin/end-card code
	 * falls back to registry defaults when empty.
	 */
	#applySkinCpDefaults = ( params: IParamsVO ): void => {
		if ( !this.utils.Type.isObject( params?.SKIN ) ) {
			return;
		}

		if ( params.SKIN.cp === undefined || params.SKIN.cp === null ) {
			params.SKIN.cp = { enabled: true } as ICP;
			return;
		}

		if ( params.SKIN.cp.enabled === undefined ) {
			params.SKIN.cp.enabled = true;
		}
	};

	/**
	 * @description SYSTEM.deviceVersion defaults to a stale hardcoded placeholder (PLBCK-7461).
	 * For real web browsers (any embedType other than HTML5, which is CTV/STB) replace it with the
	 * actual detected browser version, sourced from the same Capabilities detection that already
	 * powers the browserVersionFull New Relic attribute. CTV/STB (HTML5) keeps its existing
	 * hardware/firmware generation value from EmbedDeviceVersion untouched. Only replaces the
	 * placeholder (or our own prior 'unknown' fallback) — an explicit host-supplied deviceVersion
	 * (no host does this today, but nothing prevents it in future) is treated as an override and
	 * left untouched. Falls back to 'unknown' rather than leaving the misleading placeholder in
	 * place when the browser genuinely isn't recognized, while still retrying detection on the
	 * next load in case Capabilities just hasn't resolved yet.
	 */
	#applySystemDeviceVersionDefault = ( params: IParamsVO ): void => {
		// Fallback value for SYSTEM.deviceVersion when the browser can't be identified by
		// Capabilities.browserVersionFull
		const UNKNOWN_DEVICE_VERSION = 'unknown';

		if ( !this.utils.Type.isObject( params?.SYSTEM ) ) {
			return;
		}

		// CTV/STB (HTML5) keeps its existing hardware/firmware generation value untouched
		if ( params.SYSTEM.embedType === EmbedType.HTML5 ) {
			return;
		}

		// Value is already something other than the stale placeholder or our own 'unknown'
		// fallback — either a host-supplied override, or we already resolved a real version on a
		// prior load. Leave it be.
		if (
			params.SYSTEM.deviceVersion !== EmbedDeviceVersion.DESKTOP[ '2017' ]
			&& params.SYSTEM.deviceVersion !== UNKNOWN_DEVICE_VERSION
		) {
			return;
		}

		// Still unresolved — try to replace it with the real detected browser version, falling
		// back to 'unknown' if the browser isn't recognized (retried again on the next load).
		const detectedVersion = window?.SmartTag?.capabilities?.browserVersionFull;
		params.SYSTEM.deviceVersion = detectedVersion || UNKNOWN_DEVICE_VERSION;
	};

	/**
	 * @description Pluto TV CTV apps require `USER.profileId` to be `"0"` since Pluto CTV has no
	 * user-profile concept. Scoped to `CONTENT.partner === 'plutotv'` on a CTV/STV embed
	 * ({@link EmbedType.HTML5}); web/desktop Pluto is unaffected.
	 */
	#applyPlutoCTVProfileId = ( params: IParamsVO ): void => {
		const isPlutoCTV = params?.SYSTEM?.embedType === EmbedType.HTML5
			&& params?.CONTENT?.partner === Partner.PTV;

		if ( !isPlutoCTV || !this.utils.Type.isObject( params?.USER ) ) {
			return;
		}

		params.USER.profileId = '0';
	};

	/**
	 * @description `EmbedType.MOBILE_WEB` was `'MOBILE WEB'` (space) for ~4 years before being
	 * corrected to `'MOBILE_WEB'` (underscore, matching the documented contract) — a host still
	 * sending the old value would otherwise silently fail every `embedType === EmbedType.MOBILE_WEB`
	 * comparison in the codebase. We can't confirm every host has moved off the old value — CBS.com
	 * is migrating off Smart Tag entirely, and it's not confirmed whether any CBS.com integration
	 * will ever adopt a Smart Tag version at or beyond this fix — so this alias stays in place
	 * defensively, just in case, rather than assuming a clean cutover. Runs first in
	 * {@link normalizeParams} so every later step and every downstream consumer sees the corrected
	 * value regardless of which string variant the host sent.
	 */
	#applyEmbedTypeAlias = ( params: IParamsVO ): void => {
		const LEGACY_MOBILE_WEB_VALUE = 'MOBILE WEB';

		if ( !this.utils.Type.isObject( params?.SYSTEM ) ) {
			return;
		}

		if ( ( params.SYSTEM.embedType as string ) === LEGACY_MOBILE_WEB_VALUE ) {
			params.SYSTEM.embedType = EmbedType.MOBILE_WEB;
		}
	};

	/**
	 * @description Normalize parameters (CONTENT cleanup, SKIN.cp defaults, etc.)
	 * @param {IParamsVO} params
	 * @returns {IParamsVO}
	 */
	normalizeParams = ( params: IParamsVO ): IParamsVO => {
		this.#applyEmbedTypeAlias( params );
		this.#applySkinCpDefaults( params );
		this.#applySystemDeviceVersionDefault( params );
		this.#applyPlutoCTVProfileId( params );

		// check if params.CONTENT bucket is not undefined
		if ( this.utils.Type.isObject( params?.CONTENT ) && !this.utils.Type.isEmpty( params?.CONTENT ) ) {
			// loop over asset types and normalize them
			[
				AssetEmbedType.PID,
				AssetEmbedType.CID,
				AssetEmbedType.ASSET_URL
			].forEach(
				( assetType ) => {
					// if empty string => set to undefined
					if ( this.utils.Type.isString( params?.CONTENT[ assetType ] ) && this.utils.Type.isEmptyString( params?.CONTENT[ assetType ] ) ) {
						this?.logger?.log( 'normalizeParams:', assetType + ':', params?.CONTENT[ assetType ], '(empty string) Reset to undefined!' );
						params.CONTENT[ assetType ] = undefined;
					}
					// filter by individual property names
					switch ( assetType ) {
						case AssetEmbedType.CID:
							// IMPORTANT: reset contentId to undefined regardless => we do not play videos by contentId... yet
							params.CONTENT[ assetType ] = undefined;
							break;
					}
				}
			);
		}
		// return processed params
		return params;
	};

	/**
	 * @description Merge incoming callbacks into existing callbacks and return existing callbacks object
	 * @param {ICallbacksVO} existingCallbacks
	 * @param {ICallbacksVO} incomingCallbacks
	 * @returns {ICallbacksVO}
	 */
	mergeCallbacks = ( existingCallbacks: ICallbacksVO = {}, incomingCallbacks: ICallbacksVO = {} ): ICallbacksVO => {
		// flag requesting override of existing callbacks without conditions, default (if undefined or true ) => true - overwrite
		const overwriteExisting: boolean = incomingCallbacks?.overwrite || true;
		// loop over incoming callbacks method names
		Object.keys( incomingCallbacks ).forEach(
			( incomingMethodName ) => {
				// should we overwrite existing callbacks?
				switch ( overwriteExisting ) {
					case true: // yes, overwrite or add regardless
						if ( this.utils.Type.isFunction( incomingCallbacks[ incomingMethodName ] ) ) {
							// assign incoming callback to existing callbacks object
							existingCallbacks[ incomingMethodName ] = incomingCallbacks[ incomingMethodName ];
						}
						break;
					default: // no, do not overwrite, add new callbacks
						// if incoming callback does not exist in existing callbacks object and incoming callback is of type function
						if (
							this.utils.Type.isNullOrUndefined( existingCallbacks[ incomingMethodName ] )
							&&
							this.utils.Type.isFunction( incomingCallbacks[ incomingMethodName ] )
						) {
							// assign incoming callback to existing callbacks object
							existingCallbacks[ incomingMethodName ] = incomingCallbacks[ incomingMethodName ];
						}
				}
			}
		);
		// return processed existing callbacks object
		return existingCallbacks;
	};

	/**
	 * @description Converts namespaces set in params from string to object
	 * @param {IParamsVO} params
	 * @return {IParamsVO}
	 */
	namespaceConverter = ( params: IParamsVO ): IParamsVO => {
		if ( params?.TAG?.callback && this.utils.Type.isString( params?.TAG?.callback ) ) {
			params.TAG.callback = this.utils.Object.getDeepVal( window, String( params?.TAG?.callback ) );
		}

		if ( params?.TAG?.namespace ) {
			// loop over namespaces
			Object.keys( params?.TAG?.namespace ).forEach(
				( key ) => {
					// if namespace is defined as String
					if ( this.utils.Type.isString( params?.TAG?.namespace[ key ] ) ) {
						params.TAG.namespace[ key ] = this.utils.Object.getDeepVal( window, String( params?.TAG?.namespace[ key ] ) );
					}
				}
			);
		}

		return params;
	};

	/**
	 * @description Returns whether a fatal error is network-caused and occurring while Smart Tag
	 * already knows the connection is offline (PLBCK-8332). Gated behind `TAG.offlineFatalRecovery`
	 * (default `false` for every embed) — hosts opt in once their reconnect/reload contract is
	 * confirmed; see `IParamsTag.offlineFatalRecovery`. Checked via `code`, not `category` —
	 * avia's shared `createError()` always passes `category` as `undefined` regardless of adapter
	 * (confirmed against avia-js@2.62.1, the version Smart Tag pins), so `category` can never be
	 * used for this check on any adapter, Shaka included.
	 * @param {Pick<IPlayerAdapterErrorInfo, 'code'>} errorDetail
	 * @returns {boolean}
	 */
	isNetworkFatalWhileOffline = ( errorDetail: Pick<IPlayerAdapterErrorInfo, 'code'> ): boolean => {
		return this.tag?.params?.TAG?.offlineFatalRecovery === true
			&& window?.SmartTag?.online === false
			&& PlayerAdapterNetworkErrorCodes.has( errorDetail?.code );
	};

	/**
	 * @description Returns whether Smart Tag's offline panel should be preserved (shown, not
	 * hidden) during a load/reset or player-creation transition (PLBCK-8332). Gated behind
	 * `TAG.offlineFatalRecovery` (default `false` for every embed) — same choke point as
	 * `isNetworkFatalWhileOffline` — so a non-opted-in host (e.g. CTV) gets the exact
	 * pre-PLBCK-8332 behavior (unconditional hide) with zero change from this feature.
	 * @returns {boolean}
	 */
	shouldPreserveOfflinePanel = (): boolean => {
		return this.tag?.params?.TAG?.offlineFatalRecovery === true
			&& window?.SmartTag?.online === false;
	};
}